'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { submissionService } from '@/lib/services/submissionService';
import { createClient } from '@/lib/supabase/client';

interface LandingEnquiryPopupProps {
  onClose: () => void;
}

export default function LandingEnquiryPopup({ onClose }: LandingEnquiryPopupProps) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    course: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const courses = [
    'AI Complete Course',
    'AI Powered Digital Marketing',
    'Graphic Designing',
    'Digital Marketing',
    'Coding',
    'Video Editing',
    'Website Development',
    'App Building',
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await submissionService.createLeadSubmission(formData);
      
      if (result) {
        try {
          const supabase = createClient();
          await supabase.functions.invoke('send-lead-notification', {
            body: {
              name: formData.name,
              phone: formData.phone,
              email: formData.email,
              course: formData.course,
            },
          });
        } catch (emailError) {
          console.log('Email notification error:', emailError);
        }

        setIsSuccess(true);
        setSubmitMessage('🎉 Congratulations! You\'ve unlocked 10% OFF on your admission. Our team will contact you within 24 hours to help you get started!');
        setFormData({ name: '', phone: '', email: '', course: '' });
        
        setTimeout(() => {
          onClose();
        }, 4000);
      } else {
        setSubmitMessage('Something went wrong. Please try again later.');
      }
    } catch (error) {
      console.error('Landing enquiry submission error:', error);
      setSubmitMessage('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
          aria-label="Close popup"
        >
          <Icon name="XMarkIcon" size={24} className="text-gray-600" />
        </button>

        <div className="bg-gradient-to-br from-primary via-secondary to-primary p-8 text-center">
          <div className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 rounded-full px-4 py-2 mb-4 animate-bounce">
            <Icon name="SparklesIcon" size={18} className="text-gray-900" />
            <span className="text-xs font-bold uppercase tracking-wider">
              🎉 Limited Time: 10% OFF!
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl uppercase tracking-tighter text-white mb-3">
            Claim Your Discount
          </h2>
          <p className="text-white/90 text-lg">
            Fill in your details and get <span className="text-yellow-300 font-bold">10% OFF</span> on admission + Free Demo Lecture + Free Counselling!
          </p>
        </div>

        <div className="p-8">
          {isSuccess ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircleIcon" size={48} className="text-green-600" />
              </div>
              <p className="text-lg text-foreground font-semibold mb-2">{submitMessage}</p>
              <p className="text-muted-foreground">This popup will close automatically...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-foreground mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label htmlFor="course" className="block text-sm font-semibold text-foreground mb-2">
                  Course Interested In *
                </label>
                <select
                  id="course"
                  name="course"
                  value={formData.course}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all bg-white"
                >
                  <option value="">Select a course</option>
                  {courses.map((course) => (
                    <option key={course} value={course}>
                      {course}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-6">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="GiftIcon" size={20} className="text-primary" />
                  What You Get:
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">10% Discount</span> on admission fees</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">Free Demo Lecture</span> to experience our teaching</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">Free Career Counselling</span> from experts</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="CheckCircleIcon" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground"><span className="font-semibold text-foreground">Priority Support</span> for your queries</span>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-primary to-secondary text-white py-4 rounded-xl font-bold uppercase tracking-wider hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Claim 10% Discount Now!
                    <Icon name="ArrowRightIcon" size={20} />
                  </>
                )}
              </button>

              {submitMessage && !isSuccess && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                  <Icon name="ExclamationCircleIcon" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{submitMessage}</p>
                </div>
              )}

              <p className="text-xs text-muted-foreground text-center">
                By submitting this form, you agree to be contacted by Apollobits Academy regarding your enquiry.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}